import { BizAppConfig } from './application.manifest';

export type BrandLocale = 'zh-CN' | 'zh-TW' | 'en-US' | 'ja-JP';

type BrandTextKey = 'appName' | 'footerCopyright';
type BrandLinkRel = 'icon' | 'shortcut icon' | 'apple-touch-icon';

const BRAND_LOCALE_STORAGE_KEYS = ['umi_locale', 'app_locale'] as const;

const BRAND_LOCALES: BrandLocale[] = ['zh-CN', 'zh-TW', 'en-US', 'ja-JP'];
const BRAND_ICON_LINK_RELS: BrandLinkRel[] = [
  'icon',
  'shortcut icon',
  'apple-touch-icon',
];

/** 平台设置中配置的品牌；空字段沿用环境变量与 public/ 内置资源 */
export type RuntimeBranding = Partial<{
  platformName: string;
  platformNameEn: string;
  logoLight: string;
  logoDark: string;
  favicon: string;
  footerCopyright: string;
}>;

const RUNTIME_BRANDING_STORAGE_KEY = 'coral_platform_branding';

// 缓存上次拿到的品牌，下次打开页面时在接口返回前就能显示正确的名称与 logo
const readCachedBranding = (): RuntimeBranding => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(RUNTIME_BRANDING_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as RuntimeBranding) : {};
  } catch {
    return {};
  }
};

let runtimeBranding: RuntimeBranding = readCachedBranding();

export const setRuntimeBranding = (branding: RuntimeBranding): void => {
  runtimeBranding = { ...branding };
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      RUNTIME_BRANDING_STORAGE_KEY,
      JSON.stringify(runtimeBranding),
    );
  } catch {
    // storage unavailable; the in-memory value still applies
  }
};

const isBrandLocale = (locale?: string): locale is BrandLocale =>
  Boolean(locale && BRAND_LOCALES.includes(locale as BrandLocale));

export const normalizeBrandLocale = (locale?: string): BrandLocale =>
  isBrandLocale(locale) ? locale : 'zh-CN';

export const getStoredBrandLocale = (): BrandLocale => {
  if (typeof window === 'undefined') {
    return 'zh-CN';
  }

  const storedLocale = BRAND_LOCALE_STORAGE_KEYS.map((key) =>
    window.localStorage.getItem(key),
  ).find(Boolean);

  return normalizeBrandLocale(storedLocale || undefined);
};

const getBrandText = (
  key: BrandTextKey,
  locale?: string,
  fallback?: string,
): string => {
  const normalizedLocale = normalizeBrandLocale(
    locale || getStoredBrandLocale(),
  );
  const runtimeText =
    key === 'appName'
      ? (normalizedLocale === 'en-US' && runtimeBranding.platformNameEn) ||
        runtimeBranding.platformName
      : runtimeBranding.footerCopyright;
  if (runtimeText) {
    return runtimeText;
  }
  const localizedText =
    key === 'appName'
      ? BizAppConfig.appNames[normalizedLocale]
      : BizAppConfig.appFooterCopyrights[normalizedLocale];

  return localizedText || fallback || '';
};

export const getBrandAppName = (locale?: string): string =>
  getBrandText('appName', locale, BizAppConfig.appName);

export const getBrandFooterCopyright = (locale?: string): string =>
  getBrandText('footerCopyright', locale, BizAppConfig.appFooterCopyright);

/**
 * public/ 下的双版本 logo。命名按「适用主题」而非图形本身颜色：
 * logo-dark 是白色图形（配深色背景），logo-light 是墨蓝图形（配浅色背景）。
 */
const BRAND_THEME_LOGOS = {
  realDark: '/logo-dark.png',
  light: '/logo-light.png',
} as const;

/** 部署配置里的默认品牌（不含平台设置的覆盖），平台设置页用它显示当前在用的名称与 logo */
export const getDeploymentBranding = () => ({
  platformName: BizAppConfig.appNames['zh-CN'] || BizAppConfig.appName || '',
  platformNameEn: BizAppConfig.appNames['en-US'] || '',
  logoLight: BRAND_THEME_LOGOS.light,
  logoDark: BRAND_THEME_LOGOS.realDark,
  favicon: BRAND_THEME_LOGOS.light,
  footerCopyright:
    BizAppConfig.appFooterCopyrights['zh-CN'] ||
    BizAppConfig.appFooterCopyright ||
    '',
});

/** 应用内头部 logo，需跟随当前主题切换，否则会出现白logo配白底看不见的情况。 */
export const getBrandHeaderLogo = (navTheme?: string): string =>
  navTheme === 'realDark'
    ? runtimeBranding.logoDark || BRAND_THEME_LOGOS.realDark
    : runtimeBranding.logoLight || BRAND_THEME_LOGOS.light;

/**
 * favicon 固定用墨蓝版本：浏览器标签栏底色由浏览器主题决定，
 * 与应用内主题无关，白色图形在浅色标签栏上会不可见。
 *
 * 这里不走 UMI_APP_APP_LOGO：该变量当前指向 /logo.png，而 public/ 下
 * 只有 logo-dark.png 与 logo-light.png，请求会落到 SPA 兜底返回 HTML。
 * 若后续要按品牌定制 favicon，应先补齐对应资源再恢复读取该变量。
 */
export const getBrandAppLogo = (): string =>
  runtimeBranding.favicon ||
  runtimeBranding.logoLight ||
  BRAND_THEME_LOGOS.light;

export const getBrandHeadLinks = (): Array<{
  rel: BrandLinkRel;
  href: string;
}> => {
  const href = getBrandAppLogo();

  if (!href) {
    return [];
  }

  return BRAND_ICON_LINK_RELS.map((rel) => ({
    rel,
    href,
  }));
};

export const syncBrandDocument = (locale?: string): string => {
  const nextTitle = getBrandAppName(locale);

  if (typeof document === 'undefined') {
    return nextTitle;
  }

  if (document.title !== nextTitle) {
    document.title = nextTitle;
  }

  const nextIconHref = getBrandAppLogo();
  if (!nextIconHref) {
    return nextTitle;
  }

  BRAND_ICON_LINK_RELS.forEach((rel) => {
    let link = document.head.querySelector(
      `link[rel="${rel}"]`,
    ) as HTMLLinkElement | null;

    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.appendChild(link);
    }

    if (link.getAttribute('href') !== nextIconHref) {
      link.setAttribute('href', nextIconHref);
    }
  });

  return nextTitle;
};
